import { LanguageObject } from 'src/app/types/languageObject';
import { Type } from '../features/articles/models/articles.models';
import { ImportedArticleType } from '../features/articles/models/drawing.models';
import { InfoGridTypes, TxGridTypes } from './models/grid.models';

export const LANGUAGE_ITEMS: LanguageObject[] = [
    { language: 'English', locale: 'en-GB', languageCode: 'En', country: 'unitedKingdom', emoji: '🇬🇧' },
    { language: 'German', locale: 'de-DE', languageCode: 'De', country: 'germany', emoji: '🇩🇪' },
    { language: 'Spanish', locale: 'es-ES', languageCode: 'Es', country: 'spain', emoji: '🇪🇸' },
];

export const CUSTOMER_INFO_TABS = [
    { text: 'label_contactPersons', key: InfoGridTypes.Contacts },
    { text: 'label_addresses', key: InfoGridTypes.Addresses },
    { text: 'bankAccounts', key: InfoGridTypes.BankAccounts },
    { text: 'documents', key: InfoGridTypes.Documents },
];

export const CUSTOMER_TX_TABS = [
    { text: 'customer_orders', key: TxGridTypes.Orders },
    // { text: 'deliveries', key: TxGridTypes.Deliveries },
    // { text: 'invoices', key: TxGridTypes.Invoices },
];

// manual fetching of customer order number filter in deliveries list needs to specify top to get more than 20 filters.
// this number is the highest possible value for top
export const MAX_INT = 2147483647;

export const SUPPORTED_LANGUAGES = LANGUAGE_ITEMS.map(item => item.languageCode.toLowerCase());

export const PREFERRED_LANGUAGE_ITEMS: { value: string; text: string }[] = LANGUAGE_ITEMS.map(item => ({
    value: item.languageCode,
    text: item.languageCode,
}));

export const DEADLINE_TYPE = ['InDays', 'InWorkdays', 'ThisMonth', 'NextMonth'] as const;
export type DeadlineType = (typeof DEADLINE_TYPE)[number];

export const RIGHT_PANEL_LIST_TYPE = {
    accordion: 'accordion',
} as const;
export type RightPanelListType = (typeof RIGHT_PANEL_LIST_TYPE)[keyof typeof RIGHT_PANEL_LIST_TYPE];

export const RIGHT_PANEL_TYPE = {
    customer: 'customer',
    bankDetails: 'bankDetails',
    addresses: 'addresses',
    contactPersons: 'contactPersons',
    documents: 'documents',
    articles: 'articles',
    orders: 'orders',
    positions: 'positions',
    deliveries: 'deliveries',
    invoices: 'invoices',
} as const;
export type RightPanelType = (typeof RIGHT_PANEL_TYPE)[keyof typeof RIGHT_PANEL_TYPE];

export const RIGHT_PANEL_BUTTON_ACTIONS = {
    details: 'details',
    deleteSelected: 'deleteSelected',
    close: 'close',
    edit: 'edit',
    download: 'download',
    delete: 'delete',
    editCustomer: 'editCustomer',
    duplicate: 'duplicate',
    orderDetails: 'orderDetails',
    editOrder: 'editOrder',
    deliveryDetails: 'deliveryDetails',
    createDelivery: 'createDelivery',
    invoiceDetails: 'invoiceDetails',
    editDelivery: 'editDelivery',
    editInvoice: 'editInvoice',
    articleDetails: 'articleDetails',
    editArticle: 'editArticle',
    bulkUpdateDeliveryDate: 'bulkUpdateDeliveryDate',
    sendEmail: 'sendEmail',
    bulkSendDocumentsByEmail: 'bulkSendDocumentsByEmail',
    setReadyForDelivery: 'setReadyForDelivery',
    bulkDelivery: 'bulkDelivery',
    bulkInvoice: 'bulkInvoice',
    createInvoice: 'create_invoice',
} as const;
export type RightPanelButtonAction = (typeof RIGHT_PANEL_BUTTON_ACTIONS)[keyof typeof RIGHT_PANEL_BUTTON_ACTIONS];
export const SETTINGS_NAME = ['general', 'notifications', 'features', 'actions', 'number-format'] as const;
export type SettingsNameType = (typeof SETTINGS_NAME)[number];

// Accepted file formats for document uploads (matches backend FileFormat enum)
export const ACCEPTED_FILE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.pdf', '.json', '.geo'] as const;
export const ACCEPTED_FILE_EXTENSIONS_STRING = ACCEPTED_FILE_EXTENSIONS.join(',');

export const US_STATES: string[] = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
    'DC',
];

export const TYPE_TO_IMPORTED_ARTICLE_TYPE: Record<Type, ImportedArticleType> = {
    [Type.Part]: ImportedArticleType.Part,
    [Type.Assembly]: ImportedArticleType.Assembly,
    [Type.Sheet]: ImportedArticleType.Part,
    [Type.Tube]: ImportedArticleType.Tube,
    [Type.Other]: ImportedArticleType.Other,
};

export enum State {
    CH_AG = 'CH_AG',
    CH_AI = 'CH_AI',
    CH_AR = 'CH_AR',
    CH_BL = 'CH_BL',
    CH_BS = 'CH_BS',
    CH_BE = 'CH_BE',
    CH_FR = 'CH_FR',
    CH_GE = 'CH_GE',
    CH_GL = 'CH_GL',
    CH_GR = 'CH_GR',
    CH_JU = 'CH_JU',
    CH_LU = 'CH_LU',
    CH_NE = 'CH_NE',
    CH_NW = 'CH_NW',
    CH_OW = 'CH_OW',
    CH_SG = 'CH_SG',
    CH_SH = 'CH_SH',
    CH_SZ = 'CH_SZ',
    CH_SO = 'CH_SO',
    CH_TG = 'CH_TG',
    CH_TI = 'CH_TI',
    CH_UR = 'CH_UR',
    CH_VS = 'CH_VS',
    CH_VD = 'CH_VD',
    CH_ZG = 'CH_ZG',
    CH_ZH = 'CH_ZH',
    ES_AN = 'ES_AN',
    ES_AR = 'ES_AR',
    ES_AS = 'ES_AS',
    ES_IB = 'ES_IB',
    ES_CN = 'ES_CN',
    ES_CB = 'ES_CB',
    ES_CM = 'ES_CM',
    ES_CL = 'ES_CL',
    ES_CT = 'ES_CT',
    ES_EX = 'ES_EX',
    ES_GA = 'ES_GA',
    ES_RI = 'ES_RI',
    ES_MD = 'ES_MD',
    ES_MC = 'ES_MC',
    ES_NC = 'ES_NC',
    ES_PV = 'ES_PV',
    ES_VC = 'ES_VC',
    ES_CE = 'ES_CE',
    ES_ML = 'ES_ML',
    DE_BW = 'DE_BW',
    DE_BY = 'DE_BY',
    DE_BE = 'DE_BE',
    DE_BB = 'DE_BB',
    DE_HB = 'DE_HB',
    DE_HH = 'DE_HH',
    DE_HE = 'DE_HE',
    DE_MV = 'DE_MV',
    DE_NI = 'DE_NI',
    DE_NW = 'DE_NW',
    DE_RP = 'DE_RP',
    DE_SL = 'DE_SL',
    DE_SN = 'DE_SN',
    DE_ST = 'DE_ST',
    DE_SH = 'DE_SH',
    DE_TH = 'DE_TH',
    AT_NO = 'AT_NOE',
    AT_OO = 'AT_OOE',
    AT_ST = 'AT_ST',
    AT_B = 'AT_B',
    AT_K = 'AT_K',
    AT_S = 'AT_S',
    AT_T = 'AT_T',
    AT_V = 'AT_V',
    AT_W = 'AT_W',
}
